import { useState, useEffect, useContext, useRef, useMemo, useCallback, useTransition } from 'react';
import ContextStates from '../../context/ContextStates';
import { FaPen, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { Url } from '../Url';
import FooterVenta from '../footerVenta';
import SearchAutoComplete from '../searchAutoComplete';

const currencyAR = (n) =>
    Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 });

const FormVentas = () => {
    const [cantidad, setCantidad] = useState('');
    const [precioUnitario, setPrecioUnitario] = useState('');
    const [isPending, startTransition] = useTransition();

    const {
        productos, setProductos, codigo, setCodigo, venta, setVenta,
        setProductoSelected, setViewOpen, modify, setFormView, precio,
        searchTerm, setSearchTerm, presupuesto, setEditarList,
        porcentajeDesc, descAux, setDescAux
    } = useContext(ContextStates);

    const inputRef = useRef(null);
    const cantRef = useRef(null);

    // ---------- 1) Cargar productos cuando cambie "modify"
    useEffect(() => {
        const getProductos = async () => {
            setEditarList(false);
            const formData = new FormData();
            formData.append('id', 1);
            try {
                const { data } = await axios.post(Url + 'api/listadoproductos.php', formData);
                setProductos(Array.isArray(data?.results) ? data.results : []);
            } catch (e) {
                console.error('Error listadoproductos:', e);
            }
        };
        getProductos();
    }, [modify, setEditarList, setProductos]);

    // ---------- 2) Índice O(1) por código
    const productosPorCodigo = useMemo(() => {
        const map = new Map();
        (productos || []).forEach(p => map.set(String(p.codigo || ''), p));
        return map;
    }, [productos]);

    // ---------- 3) Helpers de precio / totales (numérico)
    const getPrecioUnitario = useCallback((p) => {
        if (!p) return 0;
        if (precio) return Number(p.preciopormayor || 0);
        if (p.tipopromo === '1' || p.tipopromo === '2') return Number(p.preciopromocion || 0);
        return Number(p.precio || 0);
    }, [precio]);

    const calcTotalNumber = useCallback((item) => {
        const cant = Number(item.cantidad || 0);
        const basePrecio = precio
            ? Number(item.preciopormayor || 0)
            : (item.tipopromo === '1' || item.tipopromo === '2')
                ? Number(item.preciopromocion || 0)
                : Number(item.precio || 0);

        if (item.tipopromo === '3') {
            const minimo = Number(item.cantidadminima || 0);
            const desc = Number(item.cantidaddescuento || 0);
            if (cant >= minimo && minimo > 0) {
                const promos = Math.floor(cant / minimo);
                const cantPagas = cant - (desc * promos);
                return cantPagas * basePrecio;
            }
        }
        return cant * basePrecio;
    }, [precio]);

    // ---------- 4) Cuando cambia 'codigo', setear search y precio unitario
    useEffect(() => {
        if (!codigo) return;
        const p = productosPorCodigo.get(String(codigo));
        if (!p) return;

        if (p.descripcion && p.descripcion !== searchTerm) {
            setSearchTerm(p.descripcion);
        }
        const pu = getPrecioUnitario(p);
        if (String(pu) !== String(precioUnitario)) {
            setPrecioUnitario(pu);
        }
    }, [codigo, productosPorCodigo, searchTerm, setSearchTerm, getPrecioUnitario, precioUnitario]);

    // ---------- 5) Agregar/Sumar ítem
    const addItem = useCallback(async (codigoStr, qty = 1) => {
        const code = String(codigoStr || '').trim();
        if (!code) return;

        const p = productosPorCodigo.get(code);
        if (!p) {
            // Si no existe el código, mantenemos foco en código
            requestAnimationFrame(() => inputRef.current?.focus());
            return;
        }

        const qtyNum = Number(qty || 1);
        if (Number.isNaN(qtyNum) || qtyNum <= 0) return;

        // a) si ya existe en la venta, solo sumamos cantidad en el cliente (evita viaje al server para sumar)
        const idx = (venta || []).findIndex(v => String(v.codigo) === code);
        if (idx >= 0) {
            const nuevaVenta = [...venta];
            const actual = { ...nuevaVenta[idx] };
            actual.cantidad = Number(actual.cantidad || 0) + qtyNum;
            // recalcular subtotal en cliente si es que lo guardás
            nuevaVenta[idx] = actual;
            setVenta(nuevaVenta);
            // limpiar y enfocar
            setCodigo('');
            setCantidad('');
            setSearchTerm('');
            setPrecioUnitario('');
            requestAnimationFrame(() => inputRef.current?.focus());
            return;
        }

        // b) si no existe, hacemos el add al backend
        const toAdd = { ...p, cantidad: qtyNum };
        const formData = new FormData();
        formData.append('venta', JSON.stringify(venta || []));
        formData.append('producto', JSON.stringify(toAdd));

        try {
            const { data } = await axios.post(Url + 'api/addProducto.php', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setVenta(Array.isArray(data?.results) ? data.results : []);
            setSearchTerm('');
            setCodigo('');
            setCantidad('');
            setPrecioUnitario('');

            if ((descAux !== '' && descAux !== 0) && porcentajeDesc === '') {
                setDescAux('');
            }
        } catch (e) {
            console.error('Error addProducto:', e);
        } finally {
            requestAnimationFrame(() => inputRef.current?.focus());
        }
    }, [productosPorCodigo, venta, setVenta, setCodigo, setCantidad, setSearchTerm, setPrecioUnitario, descAux, porcentajeDesc, setDescAux]);

    // ---------- 6) Submit del form (Enter en cualquier input)
    const onSubmit = useCallback((e) => {
        e.preventDefault();
        // Si el Enter vino desde el campo código → agregar con 1
        const activeId = document.activeElement?.id;
        if (activeId === 'codigo') {
            startTransition(() => addItem(codigo, 1));
            return;
        }
        // Si vino desde cantidad → usar esa cantidad si hay código
        if (activeId === 'cantidad' && codigo) {
            startTransition(() => addItem(codigo, cantidad || 1));
            return;
        }
        // Botón o Enter genérico: priorizar cantidad si existe, sino 1
        if (codigo) {
            startTransition(() => addItem(codigo, cantidad || 1));
        }
    }, [addItem, codigo, cantidad]);

    // ---------- 7) Handlers de acciones en grilla
    const handleDelete = useCallback((item) => {
        setVenta((venta || []).filter(v => (v.id ?? v.codigo) !== (item.id ?? item.codigo)));
    }, [setVenta, venta]);

    const handleSet = useCallback((item) => {
        setProductoSelected(item);
        setFormView(1);
        setViewOpen(true);
    }, [setProductoSelected, setFormView, setViewOpen]);

    // ---------- 8) Render
    return (
        <div className="flex flex-col min-h-screen gap-2">
            <div className="transition-all w-full mt-3 flex">
                <div className={`w-full md:w-10/12 mx-auto mt-1 p-1 md:p-3 rounded-lg md:flex ${presupuesto ? 'bg-red-200' : !precio ? 'bg-indigo-200' : 'bg-orange-200'}`}>
                    {/* Un solo FORM para capturar Enter desde ambos inputs */}
                    <form className="w-full md:w-11/12 flex" onSubmit={onSubmit}>
                        <div className="sm:items-start px-2 lg:px-3">
                            <label htmlFor="codigo" className="block text-gray-600 uppercase font-bold">Código</label>
                            <input
                                type="text"
                                className="py-2 px-2 lg:px-3 w-full rounded"
                                id="codigo"
                                ref={inputRef}
                                placeholder="Código de producto"
                                value={codigo}
                                onChange={(e) => setCodigo(e.target.value)}
                                onKeyDown={(e) => {
                                    // Seguridad extra: si presiona Enter aquí, agregamos con 1.
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        startTransition(() => addItem(codigo, 1));
                                    }
                                }}
                                autoFocus
                                autoComplete="off"
                                inputMode="numeric"
                            />
                        </div>

                        <div className="w-full md:w-1/2 sm:items-start px-2 lg:px-3">
                            <SearchAutoComplete items={productos} />
                        </div>

                        <div className="w-1/10 sm:items-start px-2 lg:px-3">
                            <label htmlFor="punitario" className="block text-gray-600 uppercase font-bold">Precio unit.</label>
                            <input
                                type="text"
                                className="py-2 px-2 lg:px-3 w-full rounded bg-gray-50"
                                id="punitario"
                                value={precioUnitario}
                                readOnly
                            />
                        </div>

                        <div className="w-1/10 sm:items-start px-2 lg:px-3">
                            <label htmlFor="cantidad" className="block text-gray-600 uppercase font-bold">Cantidad</label>
                            <input
                                type="number"
                                className="py-2 px-3 w-full rounded"
                                id="cantidad"
                                ref={cantRef}
                                placeholder="Cantidad"
                                value={cantidad}
                                onChange={(e) => setCantidad(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        if (codigo) startTransition(() => addItem(codigo, cantidad || 1));
                                    }
                                }}
                                inputMode="numeric"
                            />
                        </div>

                        <div className="w-1/10 sm:items-start px-2 lg:px-3 pt-4">
                            <button
                                type="submit"
                                className="w-full shadow-md p-3 bg-blue-600 text-gray-100 hover:bg-blue-500 hover:cursor-pointer transition-all uppercase font-semibold rounded mt-1 disabled:opacity-60"
                                disabled={isPending}
                            >
                                {isPending ? 'Agregando...' : 'Agregar'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="w-11/12 sm:w-10/12 mx-auto mt-3 mb-20">
                {(venta && venta.length > 0) && (
                    <table className="table-auto mt-0 w-full border border-collapse border-gray-300">
                        <thead>
                            <tr className="border border-collapse border-gray-300 bg-blue-100">
                                <th className="border">Cantidad</th>
                                <th className="border">Código</th>
                                <th className="border">Descripción</th>
                                <th className="border">Precio</th>
                                <th className="border">Subtotal</th>
                                <th className="border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {venta.map((item) => {
                                const subtotalNum = calcTotalNumber(item);
                                const precioMostrar = precio
                                    ? Number(item.preciopormayor || 0)
                                    : (item.tipopromo === '1' || item.tipopromo === '2')
                                        ? Number(item.preciopromocion || 0)
                                        : Number(item.precio || 0);

                                return (
                                    <tr key={item.id ?? item.codigo} className="border-b border-gray-200 hover:bg-slate-200">
                                        <td className="border px-3 text-center">{item.cantidad}</td>
                                        <td className="border px-3 text-center">{item.codigo}</td>
                                        <td className="border px-3">{item.descripcion}</td>

                                        <td className={`border px-3 text-right ${(item.tipopromo === '1' || item.tipopromo === '2') ? 'bg-green-200' : ''}`}>
                                            {currencyAR(precioMostrar)}
                                        </td>

                                        <td className={`border px-3 text-right ${(item.tipopromo === '3' && Number(item.cantidad) >= Number(item.cantidadminima)) ? 'bg-green-200' : ''}`}>
                                            {currencyAR(subtotalNum)}
                                        </td>

                                        <td className="border px-3 text-center">
                                            <button className="bg-gray-400 p-3 rounded-md text-white ml-3" onClick={() => handleSet(item)}>
                                                <FaPen />
                                            </button>
                                            <button className="bg-red-400 p-3 rounded-md text-white ml-3" onClick={() => handleDelete(item)}>
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <FooterVenta />
        </div>
    );
};

export default FormVentas;
